import type { FailureReason, FiatFlow, Quote } from '@funkit/connect-core'
import type { TFunction } from 'i18next'

import type { FeeDetailsRow } from '../screens/FeeDetailsScreen'
import { formatFiat, formatPercent } from '../utils/formatAmount'
import { ctaLabel } from './ctaLabel'

/**
 * The live quote's fee, worded exactly as the static step words its computed
 * one. The screen fades the whole line while a re-quote is due, so there is
 * nothing here to hold still — no splitting the number out of the sentence.
 * No fee renders until a quote priced this amount; the empty state keeps the
 * quick amounts up instead.
 */
export function quoteFee(
  quote: Quote | undefined,
  amount: string,
  t: TFunction,
): string | undefined {
  if (quote === undefined || !(Number(amount) > 0)) {
    return undefined
  }
  // The quote carries its own currency, which is not always USD.
  return t('depositAmount.fee', {
    amount: formatFiat(Number(quote.totalFee), quote.fiat.currency),
  })
}

/** The state's own recovery, when its step response carries one. */
export function recoveryCta(
  flow: FiatFlow,
  t: TFunction,
): { label: string; onPress: () => void } | undefined {
  // Terminal failure states may still carry transitions for polling or for a
  // backend recovery path. The failure metadata is authoritative for what a
  // buyer can do: never turn a non-retryable NONE/CONTACT_SUPPORT refusal
  // (notably an amount above a provider's hard maximum) into "Try again".
  const state = flow.stepResponse?.state
  const failure =
    state?.kind === 'ORDER' && state.phase === 'CREATED'
      ? (state.failureReason ?? state.error)
      : undefined
  if (
    failure !== undefined &&
    !failure.retryable &&
    failure.recovery !== 'REQUOTE' &&
    failure.recovery !== 'RETRY_PAYMENT'
  ) {
    return undefined
  }
  const cta = flow.ctas[0]
  if (cta === undefined) {
    return undefined
  }
  return {
    label: ctaLabel(cta, t),
    onPress: () => void flow.submit(cta),
  }
}

/** The refreshed price a post-KYC review carries. */
export function reviewDetail(flow: FiatFlow): string | undefined {
  const state = flow.stepResponse?.state
  if (state?.kind !== 'ORDER' || state.phase !== 'AWAITING_CONFIRMATION') {
    return undefined
  }
  return `$${state.quote.fiat.amount}`
}

/**
 * The quote's fee breakdown, as the disclosure drawer's rows.
 *
 * - The fee is inside the charge, not added to it: `fiat.amount` is what the
 *   card is billed and the crypto is priced on what's left, so the amount row
 *   is the remainder and the total is the charge itself.
 * - One fee row, off `totalFee` rather than the provider's own `feeBreakdown`
 *   labels: those name the rail ("Coinbase fee"), which the drawer must not,
 *   and only the total keeps the three rows adding up.
 */
export function quoteFeeRows(
  quote: Quote | undefined,
  t: TFunction,
): FeeDetailsRow[] {
  if (quote === undefined) {
    return []
  }
  const charge = Number(quote.fiat.amount)
  const totalFee = Number(quote.totalFee)
  const inQuoteCurrency = (value: number) =>
    formatFiat(value, quote.fiat.currency)
  const rate = formatPercent(charge > 0 ? totalFee / charge : 0)
  return [
    {
      label: t('feeDetails.amount'),
      value: inQuoteCurrency(charge),
    },
    {
      label: t('feeDetails.fee', { rate }),
      rate,
      value: inQuoteCurrency(totalFee),
    },
    {
      label: t('feeDetails.youllGet'),
      value: inQuoteCurrency(charge - totalFee),
      isTotal: true,
    },
  ]
}

/** The rail disclosure, priced from the quote rather than host configuration. */
export function quoteFeeSummary(
  quote: Quote | undefined,
  method: string | undefined,
  t: TFunction,
): string | undefined {
  if (quote === undefined || method === undefined) {
    return undefined
  }
  const charge = Number(quote.fiat.amount)
  const totalFee = Number(quote.totalFee)
  if (
    !Number.isFinite(charge) ||
    charge <= 0 ||
    !Number.isFinite(totalFee) ||
    totalFee < 0
  ) {
    return undefined
  }
  return t('feeDetails.summaryDeductedFromDeposit', {
    method,
    rate: formatPercent(totalFee / charge),
  })
}

/**
 * A step's own error, worded for the banner it renders as. The contract sends
 * no message — `code` is a copy key — so the category is what the copy keys off.
 */
export function roundErrorMessage(
  error: FailureReason | undefined,
  t: TFunction,
  provider?: string,
): string | undefined {
  // The server keeps a lapsed price on the form on purpose: the answers still
  // deliver and the price is replaced once the form completes. Nothing failed,
  // so there is nothing to banner.
  if (error === undefined || isExpiredQuoteMarker(error)) {
    return undefined
  }
  if (error.category !== 'USER_ERROR') {
    return t('fiat.submitFailed')
  }
  // Keyed on `code`, because every submission error shares one category and a
  // message read off that alone can only say that something was wrong.
  switch (error.code) {
    case 'STALE_FORM':
      return t('fiat.kycFormStale')
    case 'MISSING_FIELD':
      return t('fiat.kycFormMissing')
    // The recipient already holds the value for another of its customers;
    // only a different value can go through.
    case 'PHONE_IN_USE':
      return t('fiat.kycPhoneInUse', { provider: providerName(provider) })
    case 'TAX_ID_IN_USE':
      return t('fiat.kycTaxIdInUse', { provider: providerName(provider) })
    case 'UNEXPECTED_FIELD':
    case 'DUPLICATE_FIELD':
      // Neither is the user's doing: the client sent a key the round did not
      // ask for, or sent one twice.
      return t('fiat.submitFailed')
    default:
      return t('fiat.kycFormInvalid')
  }
}

/**
 * A cooling-off hold's comeback date, worded as the ISO day it names.
 *
 * The day rather than the instant, and unformatted: connect-rn takes no Intl
 * dependency, and a locale-guessed date is worse than an unambiguous one.
 */
export function onHoldDetail(flow: FiatFlow, t: TFunction): string {
  const date = isoDay(kycRetryAfter(flow))
  return date === undefined
    ? t('fiat.kycOnHoldDetail')
    : t('fiat.kycOnHoldUntil', { date })
}

/** Preserve an explicit cooling-off deadline alongside the R2 receipt. */
export function kycHoldRetryDetail(
  flow: FiatFlow,
  t: TFunction,
): string | undefined {
  const date = isoDay(kycRetryAfter(flow))
  return date === undefined ? undefined : t('fiat.kycOnHoldUntil', { date })
}

/**
 * What a rejection leaves the user. Only the terminal variant carries a
 * recovery, and support is the only one it names.
 */
export function rejectedDetail(
  flow: FiatFlow,
  t: TFunction,
): string | undefined {
  const state = flow.stepResponse?.state
  if (state?.kind !== 'KYC' || state.phase !== 'NO_ACTION_REQUIRED') {
    return undefined
  }
  return state.failureReason?.recovery === 'CONTACT_SUPPORT'
    ? t('fiat.kycRejectedSupport')
    : undefined
}

export function blockedRetryDetail(flow: FiatFlow): string | undefined {
  const state = flow.stepResponse?.state
  return state?.kind === 'BLOCKED'
    ? isoDay(state.retryAfter ?? undefined)
    : undefined
}

function kycRetryAfter(flow: FiatFlow): string | undefined {
  const state = flow.stepResponse?.state
  return state?.kind === 'KYC' && state.phase === 'NO_ACTION_REQUIRED'
    ? state.retryAfter
    : undefined
}

function isoDay(value: string | undefined): string | undefined {
  if (value === undefined) {
    return undefined
  }
  return Number.isNaN(new Date(value).getTime())
    ? undefined
    : value.slice(0, 10)
}

/** The marker a KYC round carries when its quote lapsed while the form was open. */
export function isExpiredQuoteMarker(error: FailureReason): boolean {
  return error.category === 'EXPIRED' && error.recovery === 'REQUOTE'
}

/** The recipient as the buyer knows it; the wire id is upper case. */
export function providerName(provider: string | undefined): string {
  if (provider === undefined) {
    return 'provider'
  }
  return provider.charAt(0) + provider.slice(1).toLowerCase()
}
